import { Button, Col, Row } from 'react-bootstrap';
import React, { useEffect, useMemo } from 'react';
import LeftSide from '../main/left-side';
import Navbar from '../navigation/navbar';
import TitleCard from '../main/title-card';
import LargeCard from '../main/large-card';
import useTranslate from '../useTranslate';
import { Link } from 'react-router';
import { usePbDispatch, usePbSelector } from '../../store/pbStoreHooks';
import { LoadMansions } from '../../store/mansionsSlice';
import {
    CreateOperationalContact,
    OperationalContactWriteDto,
    ResetCreateOperationalContact,
} from '../../store/operationalContactsSlice';
import OperationalContactAssignmentsForm, {
    MansionAssignments,
} from './operational-contact-assignments-form';
import { RequestStatus } from '../../store/network/managedRequest';
import {
    goBack,
    goToOperationalContactOverview,
} from '../../nav/navigationService';
import { Form, Formik } from 'formik';
import OperationalContactBasicForm, {
    filterOperationalContactValues,
    validateOperationalContact,
} from './operational-contact-basic-form';
import { PbRequest } from '../../store/pbRequest';

const WIDTH_OF_12 = 8;

type OperationalContactCreateValues = OperationalContactWriteDto &
    MansionAssignments;

function OperationalContactCreate() {
    const dispatch = usePbDispatch();
    const strings = useTranslate('OperationalContactCreate');
    const mansionsById = usePbSelector((state) => state.mansions.mansionsById);
    const countryIsoCode = usePbSelector(
        (state) =>
            state.countriesDetails.idToCountry[
                state.partnerManager.selectedPartner?.country_id ?? 1
            ]?.isocode1
    );
    const createRequest = usePbSelector(
        (state) => state.pbRequest[PbRequest.POST_OPERATIONAL_CONTACT]
    );
    const initialValues = useMemo<OperationalContactCreateValues>(() => {
        const preSelectedMansionIds =
            Object.values(mansionsById).length === 1
                ? [Object.values(mansionsById)[0].id]
                : [];
        return {
            name: '',
            email: '',
            phone1: '',
            phone2: '',
            mansionIds: preSelectedMansionIds,
        };
    }, [mansionsById]);

    useEffect(() => {
        dispatch(LoadMansions());
        return () => {
            dispatch(ResetCreateOperationalContact());
        };
    }, []);

    useEffect(() => {
        if (createRequest.status === RequestStatus.SUCCESS) {
            goToOperationalContactOverview();
        }
    }, [createRequest.status]);

    return (
        <Navbar nav>
            <Row>
                <LeftSide>
                    <TitleCard
                        title={strings.title}
                        left={{
                            content: (
                                <Link
                                    to="/operational-contacts"
                                    className={
                                        'icon icon-wew-arrow-left icon-sm'
                                    }
                                />
                            ),
                            title: strings.back,
                        }}
                    />
                </LeftSide>
                <Col xs={9} className="col-middle">
                    <Formik
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        validate={validateOperationalContact}
                        enableReinitialize
                    >
                        {renderCards}
                    </Formik>
                </Col>
            </Row>
        </Navbar>
    );

    function renderCards() {
        return (
            <Form>
                <Row>
                    <Col xs={WIDTH_OF_12}>
                        <LargeCard
                            title={strings.title}
                            type={'card-edit'}
                            description={strings.description}
                        >
                            <OperationalContactBasicForm
                                active
                                defaultCountry={countryIsoCode}
                            />
                        </LargeCard>
                    </Col>
                </Row>
                <Row>
                    <Col xs={WIDTH_OF_12}>
                        <LargeCard
                            title={strings.titleAssignments}
                            type={'card-edit'}
                            description={strings.descriptionAssignments}
                        >
                            <OperationalContactAssignmentsForm
                                idToMansion={mansionsById}
                            />
                        </LargeCard>
                    </Col>
                </Row>
                <Row>
                    <Col xs={WIDTH_OF_12}>
                        <Button type="submit" className="btn btn-primary">
                            {strings.actionCreate}
                        </Button>
                        <Button
                            onClick={handleCancel}
                            className="btn btn-default"
                        >
                            {strings.back}
                        </Button>
                    </Col>
                </Row>
            </Form>
        );
    }

    function handleSubmit(values: OperationalContactCreateValues) {
        const { mansionIds, ...createValues } = values;
        dispatch(
            CreateOperationalContact(
                filterOperationalContactValues(createValues),
                mansionIds
            )
        );
    }

    function handleCancel() {
        goBack();
    }
}

export default OperationalContactCreate;
